import { Box } from "../../box";
import { forwardRef, useMemo } from "react";
import { cssModule, isNil, mergeProps, useEventCallback, useSlots } from "../../shared";
import { ItemKeyProp } from "./Menu";
import { Text } from "../../typography";
import { TooltipTrigger } from "../../tooltip";
import { useMenuContext } from "./MenuContext";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const DefaultElement = "li";
const RoleBySelectionMode = {
  multiple: "menuitemcheckbox",
  none: "menuitem",
  single: "menuitemradio"
};
export function InnerMenuItem({
  active,
  as = DefaultElement,
  children,
  disabled,
  focus,
  forwardedRef,
  hover,
  id,
  item: {
    key,
    tooltip
  },
  ...rest
}) {
  var _text$props, _description$props;

  const {
    onSelect,
    selectedKeys,
    selectionMode
  } = useMenuContext();
  const handleClick = useEventCallback(event => {
    if (!disabled) {
      onSelect(event, key);
    }
  });
  const handleMouseEnter = useEventCallback(event => {
    event.target.focus();
  });
  const {
    avatar,
    description,
    "end-icon": endIcon,
    icon,
    text
  } = useSlots(children, useMemo(() => ({
    _: {
      defaultWrapper: Text
    },
    avatar: (avatarElement, slotElements) => {
      return {
        className: "o-ui-menu-item-option-avatar",
        size: isNil(slotElements.description) ? "2xs" : "md"
      };
    },
    description: {
      className: "o-ui-menu-item-description",
      id: `${id}-description`,
      size: "md"
    },
    "end-icon": {
      className: "o-ui-menu-item-end-icon",
      size: "sm"
    },
    icon: (iconElement, slotElements) => {
      return {
        className: "o-ui-menu-item-start-icon",
        size: isNil(slotElements.description) ? "sm" : "lg"
      };
    },
    text: {
      className: "o-ui-menu-item-label",
      id: `${id}-label`
    }
  }), [id]));
  const labelId = text === null || text === void 0 ? void 0 : (_text$props = text.props) === null || _text$props === void 0 ? void 0 : _text$props.id;
  const descriptionId = description === null || description === void 0 ? void 0 : (_description$props = description.props) === null || _description$props === void 0 ? void 0 : _description$props.id;
  const role = RoleBySelectionMode[selectionMode];

  const itemMarkup = /*#__PURE__*/_jsxs(Box, { ...mergeProps(rest, {
      [ItemKeyProp]: key,
      "aria-checked": role !== RoleBySelectionMode.none ? !disabled && selectedKeys.includes(key) : undefined,
      "aria-describedby": description && descriptionId,
      "aria-disabled": disabled,
      "aria-labelledby": labelId,
      as,
      className: cssModule("o-ui-menu-item", description && "has-description", active && "active", focus && "focus", hover && "hover"),
      id,
      onClick: !disabled ? handleClick : undefined,
      onMouseEnter: handleMouseEnter,
      ref: forwardedRef,
      role,
      // Disabled menu item are still focusable.
      tabIndex: -1
    }),
    children: [icon, avatar, text, description, endIcon]
  });

  if (!isNil(tooltip)) {
    const {
      content: tooltipContent,
      props: tooltipProps
    } = tooltip;
    return /*#__PURE__*/_jsxs(TooltipTrigger, { ...mergeProps(tooltipProps, {
        position: "left"
      }),
      children: [itemMarkup, tooltipContent]
    });
  }

  return itemMarkup;
}
InnerMenuItem.defaultElement = DefaultElement;
export const MenuItem = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerMenuItem, { ...props,
  forwardedRef: ref
}));